import { Button, LinkButton } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import * as styles from './FavoriteEntityControlsBar.css';
import { trackGoJKClick } from './trackings';
import { AddToFolderButtonContainer } from '../../containers/AddToFolderButtonContainer';
import { ChatButtonContainer } from '../../containers/ChatButtonContainer';
import { ComparisonButtonContainer } from '../../containers/ComparisonButtonContainer';
import { ComplainContainer } from '../../containers/Complain';
import { ElectronicTradingButtonContainer } from '../../containers/ElectronicTradesButtonContainer';
import { NoteButtonContainer } from '../../containers/NoteButton';
import { PhoneEarlyAccess } from '../../containers/PhoneEarlyAccess';
import { RemoveFavoriteContainer } from '../../containers/RemoveFavorite';
import { NewbuildingComparisonButtonContainer } from '../../containers/NewbuildingComparisonButtonContainer';
import { NewbuildingShowPhoneContainer } from '../../containers/NewbuildingShowPhoneContainer';
import { getIsDailyrentOnlineBookingEnabled } from '../../hooks/useIsDailyrentOnlineBookingEnabled';
import { ECommunicationType } from '../../repositories/favorites/entities/favorite_entity_for_desktop/FavoriteEntityForDesktopSchema';
import { IApplicationContext } from '../../types/applicationContext';
import { EEntityType } from '../../types/filters';
import { IPageState } from '../../types/page';
import { IFavoriteEntity } from '../../types/results';
import { TUser } from '../../types/user';
import { ApplicationContext } from '../../utils/applicationContext';
import { showDailyrentActionButton } from '../../utils/showDailyrentActionButton';
import { AddedFavoriteEntityMenu } from '../AddedFavoriteEntityMenu';
import { BookButton } from '../BookButton';
import { ClientReaction } from '../ClientReaction';
import { OnlineBookingBookButton } from '../OnlineBookingBookButton';
import { Phone } from '../Phone';
import { trackPhoneButtonClick } from '../Phone/tracking';

export interface IFavoriteEntityControlsBarStoreProps {
  page: IPageState;
  favoriteEntities: IFavoriteEntity[];
  total: number;
  user: TUser;
  printPhoneNumbers: boolean;
  isSearcherOrSub: boolean;
}

export interface IFavoriteEntityControlsBarOwnProps {
  favoriteEntity: IFavoriteEntity;
  position: number;
  isBooked: boolean;
}

export type TFavoriteEntityControlsBarProps = IFavoriteEntityControlsBarStoreProps & IFavoriteEntityControlsBarOwnProps;

/**
 * Все добавленные кнопки <button /> нужно оборачивать
 * в div, которые будут контейнерами-толкателями кнопок
 */
export class FavoriteEntityControlsBar extends React.PureComponent<TFavoriteEntityControlsBarProps> {
  public static contextType?: React.Context<IApplicationContext> | undefined = ApplicationContext;

  public render() {
    const {
      favoriteEntity,
      position,
      favoriteEntities,
      total,
      page,
      user,
      printPhoneNumbers,
      isSearcherOrSub,
      isBooked,
    } = this.props;

    const {
      entityId,
      entityType,
      note,
      phone,
      dealType,
      jkInfo,
      fullUrl,
      comparisonStatus,
      clientReactions: { likeDislikeReaction },
      communicationType = ECommunicationType.Phone,
      tradingLink,
      dailyrent,
      isEarlyAccessEnabled,
      newbuildingDynamicCalltracking,
    } = favoriteEntity;

    const { config } = this.context as IApplicationContext;
    const isDailyrentOnlineBookingEnabled = getIsDailyrentOnlineBookingEnabled(config);
    const isEarlyAccessContactsFlowEnabled = !!config.get<boolean>('EarlyAccessFavorites.Enabled');
    const shouldUsePhoneEarlyAccess = isEarlyAccessContactsFlowEnabled && !!isEarlyAccessEnabled;
    const actionType = dailyrent?.actionType;
    const { showOtaCheckDatesButton, showPartnerCheckDatesButton, hideActionButton } = showDailyrentActionButton(
      isDailyrentOnlineBookingEnabled,
      actionType,
    );

    const isElectronicTradingAvailable = !!tradingLink;

    const index = favoriteEntities.findIndex(entity => entity.entityId === entityId);
    const entity = favoriteEntities[index];

    const isNoteButtonShow = (!note || !note.length) && entityType !== 'newbuilding';
    const favoriteEntityType = entityType as unknown as EEntityType;
    const isNewbuilding = entityType === EEntityType.Newbuilding;

    const bookButton = (
      <div className={styles['book_button']}>
        <BookButton phone={phone} favoriteEntity={favoriteEntity} page={page} total={total} position={position} />
      </div>
    );
    const onlineBookingBookButton = (
      <div className={styles['book_button']}>
        <OnlineBookingBookButton
          href={entity.fullUrl}
          offersQty={total}
          entity={entity}
          position={position}
          size="XS"
        />
      </div>
    );
    const phoneButton = (
      <Phone
        phone={phone}
        entityId={entityId}
        favoriteEntities={favoriteEntities}
        page={page}
        total={total}
        isBooked={isBooked}
      />
    );
    const newbuildingShowPhoneButton = (
      <NewbuildingShowPhoneContainer
        entityId={entityId}
        phone={phone}
        siteBlockId={newbuildingDynamicCalltracking?.siteBlockId}
        onClick={() => trackPhoneButtonClick({ entity, position: index + 1, total, deviceType: page.deviceType })}
      />
    );
    const phoneEarlyAccessButton = (
      <PhoneEarlyAccess
        deviceType={page.deviceType}
        favoriteEntity={favoriteEntity}
        page={page}
        total={total}
        position={position}
        phone={phone}
      />
    );
    const electronicTradesButton = <ElectronicTradingButtonContainer favoriteEntity={favoriteEntity} />;

    const formButton = (
      <LinkButton href={`${fullUrl}?opendealrentform=true`} size="XS" theme="fill_primary" target="_blank">
        Назначить просмотр
      </LinkButton>
    );

    const actionButton = (() => {
      if (isNewbuilding) {
        return newbuildingShowPhoneButton;
      }

      if (isElectronicTradingAvailable) {
        return electronicTradesButton;
      }

      if (communicationType === ECommunicationType.Forms) {
        return formButton;
      }

      if (showPartnerCheckDatesButton) {
        return bookButton;
      }

      if (showOtaCheckDatesButton) {
        return onlineBookingBookButton;
      }

      if (hideActionButton) {
        return null;
      }

      if (shouldUsePhoneEarlyAccess) {
        return phoneEarlyAccessButton;
      }

      return phoneButton;
    })();

    return (
      <>
        <div className={styles.controls}>
          {jkInfo && (
            <div>
              <Button
                size="XS"
                theme="fill_secondary"
                onClick={() => {
                  window.open(fullUrl, '_blank');
                  trackGoJKClick(page, user, entityId, favoriteEntities, total);
                }}
              >
                Перейти к ЖК
              </Button>
            </div>
          )}
          {actionButton}
          {
            <ChatButtonContainer
              type="short"
              offer={favoriteEntity}
              position={position}
              total={total}
              deviceType={page.deviceType}
            />
          }
          {!jkInfo && <AddToFolderButtonContainer entityId={entityId} />}
          {comparisonStatus && !isNewbuilding && (
            <ComparisonButtonContainer entityId={entityId} label={fullUrl} comparisonStatus={comparisonStatus} />
          )}
          {isNoteButtonShow && <NoteButtonContainer entityId={entityId} />}
          {isNewbuilding && comparisonStatus && (
            <NewbuildingComparisonButtonContainer
              entityId={entityId}
              label={fullUrl}
              comparisonStatus={comparisonStatus}
            />
          )}
          <RemoveFavoriteContainer dealType={dealType} entityId={entityId} favoriteEntityType={favoriteEntityType} />
          {
            // Не отображаем кнопку "Пожаловаться" для ЖК
            !jkInfo && <ComplainContainer offerId={entityId} />
          }
          {!jkInfo && (
            <AddedFavoriteEntityMenu entityId={entityId} dealType={dealType} favoriteEntityType={favoriteEntityType} />
          )}
          {likeDislikeReaction && (
            <ClientReaction isSearcherOrSub={isSearcherOrSub} likeDislikeReaction={likeDislikeReaction} />
          )}
        </div>
        {printPhoneNumbers && !shouldUsePhoneEarlyAccess && (
          <div {...mergeStyles(styles.controls, styles['print-mode'])}>{phoneButton}</div>
        )}
      </>
    );
  }
}
